import { Fragment, useState } from 'react';
import { Button } from '@/components/ui/button';
import { CambioDePrecio, Precio } from '@/components/ui/precio';
import { CeldaProducto } from '@/components/ui/celda-producto';
import { Cifra, Cifras } from '@/components/ui/cifras';
import { EstadoError } from '@/components/ui/estado-vacio';
import { GrupoDeOpciones } from '@/components/ui/grupo-de-opciones';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatoCantidad } from '@/lib/utils/formato-precio';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { DesgloseSimulado, ID_BORRADOR } from './desglose-simulado';
import { pricingRulesApi } from '../services/pricing-rules-api';
import type {
  Action,
  EstadoSimulado,
  FilaSimulada,
  ResumenSimulacion,
  RuleScope,
} from '../types/pricing-rules.types';
import { nombreAutomatico } from '../lib/nueva-regla';
import { porQueDeLaFila } from '../lib/fila-en-palabras';
import { usePedidoConEspera } from '../lib/use-pedido-con-espera';
import { alcanceDelFiltro } from '../lib/lectura-del-precio';

/**
 * Paso 3 · Qué cambiaría (maqueta S4; pulido, spec 232). La próxima corrida con
 * la regla encendida: `/simular` con origen ESTRATEGIA y el borrador. Lo primero
 * que se lee es a cuántos productos les cambia el precio POR ESTA REGLA (cambio
 * aprobado nº 3 del 29-sep); «suben / bajan» es contra el precio de hoy con
 * todas las estrategias, y va de nota debajo. Por defecto se ven «los que limita».
 */

const POR_PAGINA = 10;

/** Qué productos se listan: un estado, «los que cambian por esta regla» o todos. */
export type VerSimulacion = EstadoSimulado | 'CAMBIA' | 'TODOS';

const VER: Array<{ valor: VerSimulacion; texto: string }> = [
  { valor: 'CAMBIA', texto: 'les cambia el precio' },
  { valor: 'LIMITADO', texto: 'los que limita' },
  { valor: 'SIN_PRECIO_POSIBLE', texto: 'conflicto entre reglas' },
  { valor: 'TODOS', texto: 'todos' },
];

/** Lo que se le pide a la base además del estado; `undefined` = todo el catálogo. */
type FiltroBase = { category?: string; brand?: string } | undefined;

const filtroDe = (ver: VerSimulacion, base: FiltroBase) => {
  const f = {
    ...base,
    ...(ver === 'CAMBIA' ? { cambia: true } : ver === 'TODOS' ? {} : { estado: ver }),
  };
  return Object.keys(f).length > 0 ? { filtro: f } : {};
};

/**
 * «A 7 productos les cambia el precio por esta regla», «A 1 producto le cambia…»,
 * «A ningún producto le cambia…». Sin el número (todavía no llegó), el título del
 * paso sin cifra.
 */
function tituloDeLoQueCambia(n: number | null | undefined, sinMedir = 0): string {
  if (n == null) return 'Qué cambiaría con esta regla';
  const base =
    n === 0
      ? 'A ningún producto le cambia el precio por esta regla'
      : n === 1
        ? 'A 1 producto le cambia el precio por esta regla'
        : `A ${formatoCantidad(n)} productos les cambia el precio por esta regla`;
  // Lote A del 2-oct (A2): «a ninguno» no puede esconder a los que no se midieron.
  if (sinMedir === 0) return base;
  return sinMedir === 1
    ? `${base} · a 1 no se le puede medir el margen: no tiene costo`
    : `${base} · a ${formatoCantidad(sinMedir)} no se les puede medir el margen: no tienen costo`;
}

/**
 * Lote A del 2-oct (A2): las reglas de margen miden con el costo
 * (`rangos.service.ts`: sin costo, «esta regla no puede medir el margen»).
 * Las demás no lo necesitan: para ésas no se habla del costo.
 */
const mideConElCosto = (acciones: Action[]) =>
  acciones.some((a) => a.type === 'FORCE_MIN_MARGIN' || a.type === 'FORCE_MAX_MARGIN');

/**
 * La nota de debajo de las cifras: contra el precio de hoy, con todas las
 * estrategias. «3 suben, 27 bajan y 0 quedan igual. 1 no se puede calcular: en
 * «todos» ves por qué.»
 */
function notaContraHoy(
  r: Pick<ResumenSimulacion, 'suben' | 'bajan' | 'noCambian' | 'sinDatos'>
): string {
  const n = formatoCantidad;
  const base =
    'Contra el precio de hoy, con todas las estrategias: ' +
    `${n(r.suben)} ${r.suben === 1 ? 'sube' : 'suben'}, ` +
    `${n(r.bajan)} ${r.bajan === 1 ? 'baja' : 'bajan'} y ` +
    `${n(r.noCambian)} ${r.noCambian === 1 ? 'queda' : 'quedan'} igual.`;
  // SIN_DATOS tiene tres causas (no sólo la competencia): el porqué va en cada fila.
  if (r.sinDatos === 0) return base;
  return `${base} ${n(r.sinDatos)} no se ${r.sinDatos === 1 ? 'puede' : 'pueden'} calcular: en «todos» ves por qué.`;
}

/** Con esta regla menos sin ella; null si no hay con qué comparar. */
function diferenciaDe(f: FilaSimulada): number | null {
  if (f.precioSinBorrador == null || f.estado === 'SIN_PRECIO_POSIBLE') return null;
  return Math.round((f.precioPropuesto - f.precioSinBorrador) * 100) / 100;
}

export function PasoQueCambiaria({
  acciones,
  scope,
  editandoId,
  verInicial = 'LIMITADO',
  filtroBase,
  etiquetaBorrador = 'esta regla',
}: {
  acciones: Action[];
  scope: RuleScope;
  editandoId: string | null;
  /** «¿Y si…?» abre en «les cambia el precio»: es lo que dice su número. */
  verInicial?: VerSimulacion;
  /**
   * El mismo filtro de categoría o marca con que «¿Y si…?» sacó su número: sin él
   * la base traería el catálogo entero (y podría pasar el tope de la simulación).
   */
  filtroBase?: FiltroBase;
  /** Cómo se nombra la regla nueva en el desglose: «la que probás» en «¿Y si…?». */
  etiquetaBorrador?: string;
}) {
  const [ver, setVer] = useState<VerSimulacion>(verInicial);
  const [pagina, setPagina] = useState(1);
  const [abierta, setAbierta] = useState<string | null>(null);

  const cuerpo = {
    borrador: { name: nombreAutomatico(acciones, scope), scope, actions: acciones },
    ...(editandoId ? { reemplaza: editandoId } : {}),
    origen: 'ESTRATEGIA' as const,
    ...filtroDe(ver, filtroBase),
    page: pagina,
    limit: POR_PAGINA,
  };
  const pedido = usePedidoConEspera(
    JSON.stringify(cuerpo),
    () => pricingRulesApi.simular(cuerpo),
    0
  );
  const datos = pedido.estado === 'LISTO' ? pedido.data : null;
  const r = datos?.resumen;
  // Sin costo (null en la base; un costo 0 cuenta como costo: ver el registro del plan).
  const sinMedir = r && mideConElCosto(acciones) ? r.sinCosto : 0;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold">
          {tituloDeLoQueCambia(r?.cambiaPorElBorrador, sinMedir)}
        </h3>
        <p className="text-sm text-muted-foreground">
          Simulación con los precios de hoy: cada estrategia propone su precio y después se cruzan
          todas las reglas que alcanzan a cada producto, esta incluida.
        </p>
      </div>

      {pedido.estado === 'ERROR' ? (
        <EstadoError onReintentar={pedido.reintentar}>
          {mensajeDeError(pedido.error, 'No se pudo simular. Los precios no cambiaron.')}
        </EstadoError>
      ) : !r ? (
        <Skeleton className="h-16 w-full" />
      ) : (
        <div className="space-y-1.5" data-testid="resumen-simulacion">
          <Cifras>
            <Cifra valor={r.alcanzados} rotulo="alcanza" />
            <Cifra
              valor={r.cambiaPorElBorrador ?? '—'}
              rotulo={
                r.cambiaPorElBorrador === 1
                  ? 'le cambia el precio por esta regla'
                  : 'les cambia el precio por esta regla'
              }
            />
            <Cifra
              valor={r.sinPrecioPosible}
              rotulo={
                r.sinPrecioPosible === 1
                  ? 'con un conflicto entre reglas: iría a Por decidir'
                  : 'con un conflicto entre reglas: irían a Por decidir'
              }
              tono={r.sinPrecioPosible > 0 ? 'alerta' : 'neutro'}
            />
            <Cifra
              valor={r.sinEstrategia}
              rotulo={
                r.sinEstrategia === 1
                  ? 'sin estrategia: no se mueve solo'
                  : 'sin estrategia: no se mueven solos'
              }
            />
            {sinMedir > 0 && (
              <Cifra
                valor={sinMedir}
                rotulo={
                  sinMedir === 1
                    ? 'sin costo: no se le puede medir el margen'
                    : 'sin costo: no se les puede medir el margen'
                }
                tono="alerta"
              />
            )}
          </Cifras>
          <p className="text-xs text-muted-foreground">{notaContraHoy(r)}</p>
        </div>
      )}

      <div className="flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">Ver:</span>
        <GrupoDeOpciones
          aria-label="Qué productos ver"
          opciones={VER}
          valor={ver}
          onCambiar={(v) => {
            setVer(v);
            setPagina(1);
          }}
        />
      </div>

      {datos && (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Producto</TableHead>
                <TableHead className="text-right">Hoy (Bs)</TableHead>
                <TableHead className="text-right">Sin esta regla</TableHead>
                <TableHead className="text-right">Con esta regla</TableHead>
                <TableHead className="text-right">Diferencia</TableHead>
                <TableHead>Por qué</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {datos.filas.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="py-6 text-center text-muted-foreground">
                    Ningún producto con este filtro.
                  </TableCell>
                </TableRow>
              )}
              {datos.filas.map((f) => {
                const d = diferenciaDe(f);
                const abiertaEsta = abierta === f.productId;
                return (
                  <Fragment key={f.productId}>
                    <TableRow
                      className={abiertaEsta ? 'cursor-pointer bg-muted/40' : 'cursor-pointer'}
                      onClick={() => setAbierta(abiertaEsta ? null : f.productId)}
                    >
                      <TableCell>
                        {/* El botón da el teclado y el estado al lector de pantalla; la fila
                            entera sigue siendo clickeable con el mouse. */}
                        <CeldaProducto
                          nombre={
                            <button
                              type="button"
                              className="text-left font-medium"
                              aria-expanded={abiertaEsta}
                              aria-controls={`desglose-${f.productId}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setAbierta(abiertaEsta ? null : f.productId);
                              }}
                            >
                              {f.name}{' '}
                              <span aria-hidden className="text-xs text-muted-foreground">
                                {abiertaEsta ? '▾' : '▸'}
                              </span>
                            </button>
                          }
                          detalle={f.sku}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Precio valor={f.precioHoy} />
                      </TableCell>
                      <TableCell className="text-right">
                        <Precio valor={f.precioSinBorrador} />
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {f.estado === 'SIN_PRECIO_POSIBLE' ? (
                          'Por decidir'
                        ) : (
                          <Precio valor={f.precioPropuesto} />
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {d == null ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <CambioDePrecio diferencia={d} />
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {porQueDeLaFila(f)}
                      </TableCell>
                    </TableRow>
                    {abiertaEsta && (
                      <TableRow
                        id={`desglose-${f.productId}`}
                        className="bg-muted/40 hover:bg-muted/40"
                      >
                        <TableCell colSpan={6} className="p-3">
                          <DesgloseSimulado
                            fila={f}
                            etiquetaBorrador={etiquetaBorrador}
                            idBorrador={editandoId ?? ID_BORRADOR}
                            // El resumen cuenta el total sin precio posible, no «cuántos más».
                            conteos={{
                              cambian: r?.cambiaPorElBorrador ?? null,
                              masSinPrecio: null,
                              alcance: alcanceDelFiltro(filtroBase),
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
      {datos && datos.total > POR_PAGINA && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <span className="tabular-nums text-muted-foreground">
            {formatoCantidad((pagina - 1) * POR_PAGINA + 1)}–
            {formatoCantidad(Math.min(pagina * POR_PAGINA, datos.total))} de{' '}
            {formatoCantidad(datos.total)}
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={pagina === 1}
            onClick={() => setPagina((p) => p - 1)}
          >
            Anterior
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={pagina * POR_PAGINA >= datos.total}
            onClick={() => setPagina((p) => p + 1)}
          >
            Siguiente página
          </Button>
        </div>
      )}

      <p className="rounded-md bg-muted/40 p-3 text-xs text-muted-foreground">
        {/* Ronda del QA de R4 (spec 294, D-R4-4): guardar no la hace regir. */}
        <strong>Cuándo pasa.</strong> Queda como cambio sin aplicar: rige cuando apliques las reglas
        y recalcules.
      </p>
    </div>
  );
}
